import img from './assets/as-seen-on.png'
import ape from './assets/press-ape.png'
import logos from './assets/press-logos.png'
import { SectionHeading, SectionSubheading } from './SectionHeading'

const HEADLINE = 'WELCOME TO BR1: INFINITE'
const SUBLINE = 'PAY TO SPAWN INTO A LIVE SERVER WITH OTHER PLAYERS. EARN REAL MONEY FOR EVERY ELIMINATION.'

/**
 * Two layouts from the same section: the composite artwork on desktop, the separated pieces on a
 * phone.
 *
 * Desktop is unchanged. Its copy is an overlay in the empty left half of `as-seen-on.png`, which
 * works at that width and is left exactly as it was.
 *
 * Mobile could not use it. The overlay box is pinned to the image by `bottom-1/2` so it cannot
 * grow, and the press logos sit in a band across a 2.75:1 composite, which at 375px renders them at
 * roughly a fifth of their intended size while the ape takes most of the room. Cropping the band
 * out of the composite worked but was fragile, and it cost the ape entirely.
 *
 * With `press-ape.png` and `press-logos.png` supplied separately, the phone gets each piece placed
 * on its own terms: the character to the right, and the logo bar run full bleed beneath him.
 *
 * The ape goes *behind* the bar, and his lower edge dissolves into it rather than being cut by it.
 *
 * Two things had to be true for that, and getting either wrong is visible from across the room.
 *
 * The bar needs `z-10`. The ape is absolutely positioned and the bar is not, and the painting order
 * puts *every* positioned descendant above *every* non-positioned one, whatever the document order
 * says — so the ape drew on top of the bar, his leg carrying on over the gradient and stopping in a
 * hard horizontal line. That is what the flat line across the banner was.
 *
 * And the edge has to be soft. The gradient never exceeds 50% opacity, so being behind it does not
 * hide him; a hard clip anywhere in that band just moves the flat line onto the purple. Blacking
 * out underneath the bar removes the line but leaves a few pixels of dead space above the colour,
 * because the bar's leading edge is not level — it drifts about 3px across his width. The mask
 * fades him out over 6% of his height instead, landing inside the band where the colour is coming
 * up, so the silhouette ends without ever drawing an edge. He stays faintly visible through the
 * bar on the way out, which is what the translucency is for.
 *
 * One thing the separate bar does not solve by itself. Its lettering spans only 62.9% of the image,
 * because the gradient extends well past the logos at both ends by design. Drawn at plain full
 * width the logos would come out *smaller* than the old crop managed. So the bar is scaled to 145%
 * and allowed to run off both edges, which is what the gradient is for: at 375px the lettering then
 * occupies about 91% of the screen instead of 63%.
 */
export function AsSeenOn() {
    return (
        <>
            <div className='md:hidden flex flex-col pt-8'>
                {/* One line, which is what `SectionHeading` is for. At `text-xl` with
                    `tracking-wide` this string is 18.2em of Monument Extended Ultrabold — 364px
                    against the 343px a 375px phone has after the gutter — so it wrapped, and
                    "INFINITE" sat alone on the second line. */}
                <div className='flex flex-col gap-2'>
                    <SectionHeading>{HEADLINE}</SectionHeading>
                    <SectionSubheading>{SUBLINE}</SectionSubheading>
                </div>

                {/* The mask is what ends him, not the box — so the box is his full height and does
                    no cutting. Solid to 87%, gone by 93%: 87% is just above the bar's leading edge
                    and 93% is just inside the colour, so the fade happens entirely within the band
                    where the bar is taking over. Percentages, so it holds at any screen size. */}
                <div className='relative mt-5 h-48'>
                    <img
                        src={ape}
                        alt=''
                        className='absolute right-0 top-0 h-48 w-auto max-w-none select-none [-webkit-user-drag:none] [mask-image:linear-gradient(180deg,#000_87%,transparent_93%)] [-webkit-mask-image:linear-gradient(180deg,#000_87%,transparent_93%)]'
                    />
                    <span className='absolute bottom-9 left-4 text-sm font-black uppercase tracking-[0.2em]'>
                        As seen on
                    </span>
                </div>

                {/*
                  Full bleed, and scaled past the viewport on purpose. The gradient runs off both
                  edges while the logos stay inside: at 145% the bar is 1.45x the screen, so they
                  land at roughly 4% to 95% of it.

                  The top 17% is cropped away because the asset has its own "AS SEEN ON" baked in up
                  there, at x 0.428-0.571, y 0.035-0.121. Blown up to 145% it renders about 7px tall
                  — unreadable, and a second copy of the heading right beside the real one. The cut
                  sits below the lettering and above the gradient's leading edge, which starts no
                  earlier than 0.19, so the bar keeps its own soft top for the ape to fade into.

                  Both offsets are percentages of the parent's *width* — that is what a percentage
                  margin resolves against, on either axis — so the crop holds at any screen size:
                  the image is 0.2364 of the width tall, and 17% of that is 4.02%.
                */}
                <div className='relative z-10 -mt-7 w-full overflow-hidden'>
                    <img
                        src={logos}
                        alt='Forbes, Yahoo Finance, Dexerto, HU and VentureBeat'
                        className='-mt-[4.02%] w-[145%] max-w-none -ml-[22.5%] select-none [-webkit-user-drag:none]'
                    />
                </div>
            </div>

            <div className='hidden md:block relative overflow-hidden'>
                <div className='absolute left-0 top-0 right-1/3 bottom-1/2 [container-type:size] font-bold'>
                    <div className='pl-[10cqi] pt-[12cqi]'>
                        <h2 className='font-monument tracking-wide text-[4cqi]'>{HEADLINE}</h2>
                        <div className='text-[2.5cqi]'>
                            PAY TO SPAWN INTO A LIVE SERVER WITH OTHER PLAYERS.
                            <br />
                            EARN REAL MONEY FOR EVERY ELIMINATION.
                        </div>
                    </div>
                </div>
                {/*
                  The gap on the left was in the artwork, not the layout — nothing here has padding.
                  The bar is a flat 50% alpha across its body, but its left end ramps from 0 to that
                  50% over the first 50px of a 2087px image (2.4%), so against a black page it reads
                  as the banner stopping short of the edge. The right end has no such ramp: it is
                  full strength to 2079 of 2086, which is why only one side looks wrong.

                  So the image is widened by that 2.4% and pulled left by the same amount, putting
                  the ramp off-screen and landing the first full-strength column at x=0. The right
                  edge is unmoved — 102.4% starting at -2.4% still ends at 100% — and `overflow-hidden`
                  on the parent keeps the overhang from adding a horizontal scrollbar.

                  Desktop only. The phone build above uses `press-ape.png` and `press-logos.png` and
                  never renders this composite.
                */}
                <img src={img} alt='' className='w-[102.4%] max-w-none -ml-[2.4%]' />
            </div>
        </>
    )
}
